import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type DiamondPackage = {
  id: string;
  diamonds: number;
  price: number;
  currency: string;
  savings: string | null;
  is_active: boolean;
  sort_order: number;
};

// Pack sizes the app shows, and the currencies packs can be priced in
const PACK_SIZES = [100, 500, 1200, 2500, 5000];
const CURRENCIES = [
  { code: "CDF", label: "CDF — Congolese franc (DRC)" },
  { code: "XOF", label: "XOF — CFA franc (Bénin, Côte d'Ivoire, Sénégal)" },
  { code: "XAF", label: "XAF — CFA franc (Gabon, Congo)" },
  { code: "EUR", label: "EUR — Euro" },
];

type PackageForm = { diamonds: string; currency: string; price: string; savings: string; sort_order: string };

const ENDPOINT = "/api/v1/admin/packages";
const EMPTY_FORM: PackageForm = { diamonds: "100", currency: "CDF", price: "", savings: "", sort_order: "0" };

function packageProblems(form: PackageForm) {
  const problems: string[] = [];
  const price = Number(form.price);
  if (!Number.isFinite(price) || price <= 0) problems.push("Price must be above 0");
  if (form.currency !== "EUR" && form.price && !Number.isInteger(price)) problems.push("Price must be a whole number in this currency");
  if (form.savings && form.savings.length > 20) problems.push("Savings label must be 20 characters or fewer");
  return problems;
}

export function PackagesPage() {
  const queryClient = useQueryClient();
  const packages = useQuery({
    queryKey: ["packages"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load packages");
      const payload = (await response.json()) as { packages: DiamondPackage[] };
      return payload.packages;
    },
  });

  const [form, setForm] = React.useState<PackageForm>(EMPTY_FORM);
  const problems = packageProblems(form);

  const create = useMutation({
    mutationFn: async (body: PackageForm) => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, {
        method: "POST",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          diamonds: Number(body.diamonds),
          currency: body.currency,
          price: Number(body.price),
          savings: body.savings.trim() || null,
          sortOrder: Number(body.sort_order) || 0,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message ?? "Could not create package");
      return payload;
    },
    onSuccess: () => {
      toast.success("Package created");
      setForm(EMPTY_FORM);
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Record<string, unknown> }) => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}/${id}`, {
        method: "PATCH",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message ?? "Could not update package");
      return payload;
    },
    onSuccess: () => {
      toast.success("Package updated");
      queryClient.invalidateQueries({ queryKey: ["packages"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Packages"
        description="Diamond packages and their prices in each currency. The app shows the five pack sizes of a country's currency; a country is hidden until all five are active."
      />

      <Card className="rounded-2xl">
        <CardContent className="space-y-6 p-6">
          <div>
            <p className="text-sm font-semibold">New package</p>
            <p className="text-xs text-muted-foreground">One package per diamond amount and currency.</p>
          </div>
          <div className="grid gap-4 md:grid-cols-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Diamonds</Label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.diamonds}
                onChange={(e) => setForm({ ...form, diamonds: e.target.value })}
              >
                {PACK_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size.toLocaleString("en-GB")}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Currency</Label>
              <select
                className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Price</Label>
              <Input
                inputMode="decimal"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value.trim() })}
                placeholder={form.currency === "EUR" ? "4.99" : "10000"}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Savings label</Label>
              <Input value={form.savings} onChange={(e) => setForm({ ...form, savings: e.target.value })} placeholder="5%" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Display order</Label>
              <Input inputMode="numeric" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value.trim() })} placeholder="0" />
            </div>
          </div>

          {problems.length > 0 && (
            <ul className="list-disc space-y-1 pl-5 text-sm text-destructive">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}

          <Button className="rounded-xl" disabled={create.isPending || problems.length > 0 || !form.price} onClick={() => create.mutate(form)}>
            {create.isPending ? "Creating…" : "Create package"}
          </Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl">
        <CardContent className="p-0">
          {packages.isError ? (
            <p className="p-5 text-sm text-destructive">The packages could not be loaded.</p>
          ) : packages.isLoading ? (
            <div className="space-y-3 p-5">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Diamonds</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Savings</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(packages.data ?? []).map((pkg) => (
                  <PackageRow key={pkg.id} pkg={pkg} busy={update.isPending} onSave={(body) => update.mutate({ id: pkg.id, body })} />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PackageRow({ pkg, busy, onSave }: { pkg: DiamondPackage; busy: boolean; onSave: (body: Record<string, unknown>) => void }) {
  const [price, setPrice] = React.useState(String(pkg.price));
  const [savings, setSavings] = React.useState(pkg.savings ?? "");
  const changed = Number(price) !== pkg.price || (savings.trim() || null) !== pkg.savings;

  return (
    <TableRow>
      <TableCell className="font-medium">{pkg.diamonds.toLocaleString("en-GB")}</TableCell>
      <TableCell>{pkg.currency}</TableCell>
      <TableCell>
        <Input className="h-8 w-28" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value.trim())} />
      </TableCell>
      <TableCell>
        <Input className="h-8 w-24" value={savings} onChange={(e) => setSavings(e.target.value)} placeholder="—" />
      </TableCell>
      <TableCell>{pkg.sort_order}</TableCell>
      <TableCell>
        <span className={pkg.is_active ? "text-sm text-success" : "text-sm text-muted-foreground"}>{pkg.is_active ? "Active" : "Hidden"}</span>
      </TableCell>
      <TableCell className="space-x-2 text-right">
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl"
          disabled={!changed || busy}
          onClick={() => onSave({ price: Number(price), savings: savings.trim() || null })}
        >
          Save
        </Button>
        <Button variant="ghost" size="sm" className="rounded-xl" disabled={busy} onClick={() => onSave({ isActive: !pkg.is_active })}>
          {pkg.is_active ? "Hide" : "Show"}
        </Button>
      </TableCell>
    </TableRow>
  );
}
